/**
 * Sentry 错误监控封装
 *
 * 使用方式:
 *   1. npm install @sentry/react
 *   2. 在 .env 中配置 REACT_APP_SENTRY_DSN
 *   3. 在 src/index.js 顶部 import "./sentry";
 *
 * 没装 @sentry/react 或没设置 DSN 时,所有方法都是 no-op,APP 正常跑。
 * captureException / captureMessage / setUser 在外部代码(api.js / ErrorBoundary)
 * 任何时候调都安全。
 */

import { APP_VERSION } from "./api";

let _Sentry = null;
let _initialized = false;

const DSN = process.env.REACT_APP_SENTRY_DSN || "";
const ENV = process.env.REACT_APP_ENV || process.env.NODE_ENV || "production";

// 异步初始化 — 即使没装包也不会崩
(async () => {
  if (!DSN) {
    // eslint-disable-next-line no-console
    console.info("[sentry] DSN 未配置,错误监控未启用");
    return;
  }
  try {
    // webpackIgnore:让 webpack 在 build 时跳过解析这个 import — 没装
    // @sentry/react 也不会触发 "Module not found" 警告。运行时若包不存在
    // 直接走下面 catch,不影响 APP。
    // 用 const 间接路径 + webpackIgnore 双保险,确保 webpack 完全不去 resolve。
    const sentryPkg = /* webpackIgnore: true */ "@sentry/react";
    const mod = await import(/* webpackIgnore: true */ sentryPkg);
    _Sentry = mod;
    mod.init({
      dsn: DSN,
      environment: ENV,
      release: `syj-app@${APP_VERSION}`,
      // 会话采样:生产环境 10%,开发 100%
      tracesSampleRate: ENV === "production" ? 0.1 : 1.0,
      // 不上传 PII(用户的填写内容、地址、手机号等)
      sendDefaultPii: false,
      // 允许的错误,过滤掉常见噪音
      ignoreErrors: [
        // 用户网络抖动
        "Failed to fetch",
        "NetworkError",
        "Load failed",
        // 浏览器扩展注入的脚本
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed",
        // Safari 取消的播放
        "AbortError: The play() request",
      ],
      beforeSend(event) {
        // 删除可能含 PII 的字段
        if (event.user) delete event.user.email;
        return event;
      },
    });
    _initialized = true;
    // ErrorBoundary 通过 window.__sentry__ 访问
    if (typeof window !== "undefined") {
      window.__sentry__ = {
        captureException: (e, ctx) => mod.captureException(e, ctx),
        captureMessage: (m, ctx) => mod.captureMessage(m, ctx),
      };
    }
    // eslint-disable-next-line no-console
    console.info("[sentry] 已启用 -", ENV, "v" + APP_VERSION);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[sentry] 初始化失败 (是否未安装 @sentry/react?):", e?.message || e);
  }
})();

// ─── 对外 API(同步,即使 Sentry 未就绪也安全)───────────────────────
export function captureException(err, ctx) {
  try {
    if (_Sentry) _Sentry.captureException(err, ctx);
  } catch {}
}

export function captureMessage(msg, ctx) {
  try {
    if (_Sentry) _Sentry.captureMessage(msg, ctx);
  } catch {}
}

export function setUser(user) {
  try {
    if (!_Sentry) return;
    if (!user) return _Sentry.setUser(null);
    // 只上传脱敏后的用户标识
    _Sentry.setUser({
      id: user.id ? String(user.id) : undefined,
      // 手机号脱敏: 138****8888
      username: user.phone
        ? String(user.phone).replace(/(\d{3})\d{4}(\d{4})/, "$1****$2")
        : undefined,
    });
  } catch {}
}

export function addBreadcrumb(crumb) {
  try {
    if (_Sentry) _Sentry.addBreadcrumb(crumb);
  } catch {}
}

export function isReady() {
  return _initialized;
}
